// redux/slices/smsSlice.js
"use client";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

/* ============== HELPERS ============== */

const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

const authJsonConfigWithContentType = async () => {
  const baseCfg = await authJsonConfig();
  return {
    ...baseCfg,
    headers: {
      ...baseCfg.headers,
      "Content-Type": "application/json",
    },
  };
};

/* ============== THUNKS ============== */

// Отправка SMS (приватный роут, JWT)
export const sendSms = createAsyncThunk(
  "smsMessages/sendSms",
  async (smsData, { rejectWithValue }) => {
    try {
      const cfg = await authJsonConfigWithContentType();
      const res = await axios.post(
        `${getServerUrl()}/send-sms`,
        smsData,
        cfg
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Send SMS failed (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
        error?.message ||
        "Send SMS failed"
      );
    }
  }
);

// Получение отправленных SMS по uid (приватный)
export const fetchSentSms = createAsyncThunk(
  "smsMessages/fetchSentSms",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfig();
      const res = await axios.get(
        `${getServerUrl()}/send-sms/${uid}`,
        cfg
      );

      if (res.status === 404) {
        // нет сообщений — считаем как пустой список
        return [];
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Fetch sent SMS failed (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
        error?.message ||
        "Fetch sent SMS failed"
      );
    }
  }
);

// Получение SMS по sendId (приватный)
export const fetchSmsBySendId = createAsyncThunk(
  "sms/fetchSmsBySendId",
  async (sendId, { rejectWithValue }) => {
    try {
      if (!sendId) throw new Error("sendId не передан");

      const cfg = await authJsonConfig();
      const res = await axios.get(
        `${getServerUrl()}/send-sms/by-sendId/${sendId}`,
        cfg
      );

      if (res.status === 404) {
        return null; // такого sendId нет — это норм
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Fetch SMS by sendId failed (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Error fetching SMS"
      );
    }
  }
);

// Публичный роут по sendId (без JWT)
export const fetchPublicSmsBySendId = createAsyncThunk(
  "sms/fetchPublicSmsBySendId",
  async (sendId, { rejectWithValue }) => {
    try {
      if (!sendId) throw new Error("sendId не передан");

      const base = (getServerUrl() || "").replace(/\/$/, "");
      const res = await axios.get(
        `${base}/public/by-sendId/${sendId}`,
        {
          headers: { Accept: "application/json" },
          validateStatus: () => true,
        }
      );

      if (res.status === 404) {
        return null;
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Public fetch SMS by sendId failed (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Error fetching SMS"
      );
    }
  }
);

/* ============== SLICE ============== */

const initialState = {
  selectedSms: null,
  sentMessages: [],
  status: "idle",
  error: null,
};

const smsSlice = createSlice({
  name: "sms",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // sendSms
      .addCase(sendSms.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(sendSms.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload) {
          state.sentMessages.push(action.payload);
        }
      })
      .addCase(sendSms.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Send SMS failed";
      })

      // fetchSentSms
      .addCase(fetchSentSms.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSentSms.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.sentMessages = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchSentSms.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch sent SMS failed";
      })

      // fetchSmsBySendId
      .addCase(fetchSmsBySendId.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSmsBySendId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedSms = action.payload || null;
      })
      .addCase(fetchSmsBySendId.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch SMS by sendId failed";
      })

      // fetchPublicSmsBySendId
      .addCase(fetchPublicSmsBySendId.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicSmsBySendId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedSms = action.payload || null;
      })
      .addCase(fetchPublicSmsBySendId.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Public fetch SMS by sendId failed";
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export default smsSlice.reducer;
